import { FormGroup, Label } from "reactstrap";
import { useMutation } from "urql";
import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import Select from "react-select";
import { useMutationSaveStatus } from "./useMutationSaveStatus";
import { SaveStatusIndicator } from "./SaveStatusIndicator";

export type ValueAndLabel = { value: string; label: string };

interface Props<
  TData = unknown,
  TVariables extends Record<string, unknown> = Record<string, unknown>,
> {
  labelValue: string;
  htmlTagIdAndName: string;
  value: string | null;
  mutationDocument: TypedDocumentNode<TData, TVariables>;
  buildVariables: (validatedValue: string | null) => TVariables;
  options: readonly ValueAndLabel[];
  placeholder?: string;
  isDisabled?: boolean;
}

/**
 * Select dropdown component with GraphQL mutation support.
 *
 * This component provides a searchable dropdown with automatic mutation on change.
 * Based on react-select with GraphQL integration similar to TextInputGraphQlMutate.
 *
 * @example
 * <SelectGraphQlMutate
 *     labelValue="Language"
 *     htmlTagIdAndName="language"
 *     value={song.language ?? null}
 *     mutationDocument={updateLanguageMutation}
 *     buildVariables={(value) => ({
 *         id: song.id,
 *         language: value,
 *         bucket: song.bucket
 *     })}
 *     options={iso639_3Codes}
 *     placeholder="Select language..."
 * />
 */
export function SelectGraphQlMutate<
  TData = unknown,
  TVariables extends Record<string, unknown> = Record<string, unknown>,
>({
  labelValue,
  htmlTagIdAndName,
  value,
  mutationDocument,
  buildVariables,
  options,
  placeholder = `${labelValue} auswählen...`,
  isDisabled = false,
}: Props<TData, TVariables>) {
  const [, executeMutation] = useMutation(mutationDocument);
  const {
    status: saveStatus,
    markSaving,
    markSettled,
  } = useMutationSaveStatus();

  const submit = (newValue: string | null) => {
    const variables = buildVariables(newValue);
    markSaving();
    executeMutation(variables)
      .then((result) => {
        console.debug("Mutation result", result);
        markSettled(!!result.error);
      })
      .catch((err) => {
        console.error("Mutation threw", err);
        markSettled(true);
      });
  };

  const onChange = (newValue: ValueAndLabel | null) => {
    console.log(newValue);
    submit(newValue !== null ? newValue.value : null);
  };

  return (
    <FormGroup>
      <Label htmlFor={htmlTagIdAndName} className="d-flex align-items-center">
        {labelValue}
        <SaveStatusIndicator
          htmlTagIdAndName={htmlTagIdAndName}
          status={saveStatus}
        />
      </Label>

      <Select
        id={htmlTagIdAndName}
        name={htmlTagIdAndName}
        value={options.find((option) => option.value === value)}
        onChange={onChange}
        options={options}
        isClearable={true}
        placeholder={placeholder}
        isDisabled={isDisabled}
      />
    </FormGroup>
  );
}
